/* Copyright (c) 2020-present CloudIO Inc */

import { IoDatasourceAccessV, Query } from '@cloudio-saas/datasource-types';
import { useStateXAtomValue } from '@cloudio-saas/statex';
import {
  FormLabel,
  Theme,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import VirtualList from 'components/DataGrid/grid/List';
import Icon from 'icons/Icon';
import React, { CSSProperties } from 'react';
import { useAutoSizerContext } from 'hooks/useComponentSize';
import Store from 'store/Store';
import useAppStore, { useRecord } from 'store/useAppStore';
import NoDataFound from 'utils/NoDataFound';
import {
  sidebarActiveDatasourceIdAtom,
  sidebarDataSourceAccessFilterAtom,
} from '../../atoms';
import Checkbox from '../fields/Checkbox';
import { getIoDatasourceAccessVProps } from '../../../../store/utils/utils';

const useStyles = makeStyles((theme: Theme) => ({
  listItem: {
    display: 'flex',
    alignItems: 'center',
  },
  check: {
    minWidth: 42,
    maxWidth: 42,
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  header: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

function AppCell({
  value,
  style,
  store,
  label,
  isViewOnlyUser = false,
}: {
  value: string;
  label?: string;
  style?: CSSProperties;
  store: Store<IoDatasourceAccessV>;
  isViewOnlyUser: boolean;
}): React.ReactElement {
  const classes = useStyles();
  const { record, update } = useRecord(store, value);
  const roleName = label || 'None';

  return (
    <ListItem className={classes.listItem} dense key={value} style={style}>
      <ListItemText
        sx={{
          '& > span': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
        }}
        primary={roleName}
      />
      <IconButton
        data-testid={`datasource-access-checkall-${record?.roleUid}`}
        disabled={isViewOnlyUser}
        onClick={(e) => {
          update({
            queryAllowed: 'Y',
            insertAllowed: 'Y',
            updateAllowed: 'Y',
            deleteAllowed: 'Y',
            auditAllowed: 'Y',
            exportAllowed: 'Y',
          });
        }}
        data-tip={`Check All for ${roleName}`}
      >
        <Icon disabled={isViewOnlyUser} iconName="check-double" />
      </IconButton>
      <IconButton
        data-testid={`datasource-access-uncheckall-${record?.roleUid}`}
        disabled={isViewOnlyUser}
        onClick={(e) => {
          update({
            queryAllowed: 'N',
            insertAllowed: 'N',
            updateAllowed: 'N',
            deleteAllowed: 'N',
            auditAllowed: 'N',
            exportAllowed: 'N',
          });
        }}
        data-tip={`Uncheck All for ${roleName}`}
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          {`Uncheck All for ${roleName}`}
        </span>
        <Icon disabled={isViewOnlyUser} iconName="times" />
      </IconButton>
      <Checkbox
        data-testid="queryAllowed"
        disabled={isViewOnlyUser}
        checked={record?.queryAllowed === 'Y'}
        className={classes.check}
        onChange={(e) => update({ queryAllowed: e.target.checked ? 'Y' : 'N' })}
        data-tip={`Query Allowed for ${roleName}`}
      />
      <Checkbox
        data-testid="insertAllowed"
        disabled={isViewOnlyUser}
        checked={record?.insertAllowed === 'Y'}
        className={classes.check}
        onChange={(e) =>
          update({ insertAllowed: e.target.checked ? 'Y' : 'N' })
        }
        data-tip={`Insert Allowed for ${roleName}`}
      />
      <Checkbox
        data-testid="updateAllowed"
        disabled={isViewOnlyUser}
        checked={record?.updateAllowed === 'Y'}
        className={classes.check}
        onChange={(e) =>
          update({ updateAllowed: e.target.checked ? 'Y' : 'N' })
        }
        data-tip={`Update Allowed for ${roleName}`}
      />
      <Checkbox
        data-testid="deleteAllowed"
        disabled={isViewOnlyUser}
        checked={record?.deleteAllowed === 'Y'}
        className={classes.check}
        onChange={(e) =>
          update({ deleteAllowed: e.target.checked ? 'Y' : 'N' })
        }
        data-tip={`Delete Allowed for ${roleName}`}
      />
      <Checkbox
        data-testid="auditAllowed"
        disabled={isViewOnlyUser}
        checked={record?.auditAllowed === 'Y'}
        className={classes.check}
        onChange={(e) => update({ auditAllowed: e.target.checked ? 'Y' : 'N' })}
        data-tip={`Audit Allowed for ${roleName}`}
      />
      <Checkbox
        data-testid="exportAllowed"
        disabled={isViewOnlyUser}
        checked={record?.exportAllowed === 'Y'}
        className={classes.check}
        onChange={(e) =>
          update({ exportAllowed: e.target.checked ? 'Y' : 'N' })
        }
        data-tip={`Export Allowed for ${roleName}`}
      />
      <Checkbox
        data-testid="publicDownloadAllowed"
        disabled={isViewOnlyUser}
        sx={{ visibility: record?.roleUid === 'public' ? 'visible' : 'hidden' }}
        checked={record?.publicDownloadAllowed === 'Y'}
        className={classes.check}
        onChange={(e) =>
          update({ publicDownloadAllowed: e.target.checked ? 'Y' : 'N' })
        }
        data-tip={`Download Allowed for ${roleName}`}
      />
    </ListItem>
  );
}

const dsFilter: (
  appUid: string,
  datasourceUid: string,
) => Query<IoDatasourceAccessV> = (appUid, datasourceUid) => ({
  filter: [],
  limit: 2000,
  params: { appUid, datasourceUid },
});

const DataSourceAccess = ({
  appUid,
  isViewOnlyUser = false,
}: {
  appUid: string;
  isViewOnlyUser: boolean;
}) => {
  const { store, records, recordsOrder } = useAppStore<IoDatasourceAccessV>(
    getIoDatasourceAccessVProps(appUid),
  );
  const classes = useStyles();
  const dsid = useStateXAtomValue(sidebarActiveDatasourceIdAtom);
  store.read({ props: dsFilter(appUid, dsid) });

  let filter = useStateXAtomValue(sidebarDataSourceAccessFilterAtom);
  let roles = recordsOrder;
  if (filter && records) {
    filter = filter.toLowerCase();
    roles = roles.filter(
      (id) =>
        (records[id]?.roleName?.toLowerCase().indexOf(filter) ?? -1) !== -1,
    );
  }
  const { width: w, height } = useAutoSizerContext();

  if (roles.length === 0) {
    return (
      <NoDataFound
        message={
          filter ? `No roles found matching "${filter}"!` : 'No roles found!'
        }
      />
    );
  }
  const width = Math.max(600, w);
  return (
    <List
      sx={{
        overflowY: 'hidden',
        overflowX: 'auto',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        right: 0,
      }}
    >
      <ListItem dense style={{ width, height: 40 }}>
        <ListItemText primary="" />
        <FormLabel className={classes.check}>
          <IconButton
            disabled={isViewOnlyUser}
            data-tip="Check View Allowed for all roles"
            data-testid="datasource-access-checkall-view"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { queryAllowed: 'Y' });
              });
            }}
          >
            <Icon disabled={isViewOnlyUser} iconName="check-double" />
          </IconButton>
        </FormLabel>
        <FormLabel className={classes.check}>
          <IconButton
            disabled={isViewOnlyUser}
            data-tip="Check Insert Allowed for all roles"
            data-testid="datasource-access-checkall-insert"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { insertAllowed: 'Y' });
              });
            }}
          >
            <Icon disabled={isViewOnlyUser} iconName="check-double" />
          </IconButton>
        </FormLabel>
        <FormLabel className={classes.check}>
          <IconButton
            disabled={isViewOnlyUser}
            data-tip="Check Update Allowed for all roles"
            data-testid="datasource-access-checkall-update"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { updateAllowed: 'Y' });
              });
            }}
          >
            <Icon disabled={isViewOnlyUser} iconName="check-double" />
          </IconButton>
        </FormLabel>
        <FormLabel className={classes.check}>
          <IconButton
            disabled={isViewOnlyUser}
            data-tip="Check Delete Allowed for all roles"
            data-testid="datasource-access-checkall-delete"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { deleteAllowed: 'Y' });
              });
            }}
          >
            <Icon disabled={isViewOnlyUser} iconName="check-double" />
          </IconButton>
        </FormLabel>
        <FormLabel className={classes.check}>
          <IconButton
            disabled={isViewOnlyUser}
            data-tip="Check Audit Allowed for all roles"
            data-testid="datasource-access-checkall-audit"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { auditAllowed: 'Y' });
              });
            }}
          >
            <Icon disabled={isViewOnlyUser} iconName="check-double" />
          </IconButton>
        </FormLabel>
        <FormLabel
          data-testid="datasource-access-checkall-export"
          className={classes.check}
        >
          <IconButton
            disabled={isViewOnlyUser}
            data-tip="Check Export Allowed for all roles"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { exportAllowed: 'Y' });
              });
            }}
          >
            <Icon disabled={isViewOnlyUser} iconName="check-double" />
          </IconButton>
        </FormLabel>
        <FormLabel sx={{ visibility: 'hidden' }} className={classes.check} />
      </ListItem>
      <ListItem className={classes.header} dense style={{ width, height: 40 }}>
        <ListItemText primary="" />
        <FormLabel
          data-tip="Uncheck View Allowed for all roles"
          className={classes.check}
        >
          <IconButton
            disabled={isViewOnlyUser}
            data-testid="datasource-access-uncheckall-view"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { queryAllowed: 'N' });
              });
            }}
          >
            <span aria-hidden="false" style={{ display: 'none' }}>
              Uncheck View Allowed for all roles
            </span>
            <Icon disabled={isViewOnlyUser} iconName="times" />
          </IconButton>
        </FormLabel>
        <FormLabel
          data-tip="Uncheck Insert Allowed for all roles"
          className={classes.check}
        >
          <IconButton
            disabled={isViewOnlyUser}
            data-testid="datasource-access-uncheckall-insert"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { insertAllowed: 'N' });
              });
            }}
          >
            <span aria-hidden="false" style={{ display: 'none' }}>
              Uncheck Insert Allowed for all roles
            </span>
            <Icon disabled={isViewOnlyUser} iconName="times" />
          </IconButton>
        </FormLabel>
        <FormLabel
          data-tip="Uncheck Update Allowed for all roles"
          className={classes.check}
        >
          <IconButton
            disabled={isViewOnlyUser}
            data-testid="datasource-access-uncheckall-update"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { updateAllowed: 'N' });
              });
            }}
          >
            <span aria-hidden="false" style={{ display: 'none' }}>
              Uncheck Update Allowed for all roles
            </span>
            <Icon disabled={isViewOnlyUser} iconName="times" />
          </IconButton>
        </FormLabel>
        <FormLabel
          data-tip="Uncheck Delete Allowed for all roles"
          className={classes.check}
        >
          <IconButton
            disabled={isViewOnlyUser}
            data-testid="datasource-access-uncheckall-delete"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { deleteAllowed: 'N' });
              });
            }}
          >
            <span aria-hidden="false" style={{ display: 'none' }}>
              Uncheck Delete Allowed for all roles
            </span>
            <Icon disabled={isViewOnlyUser} iconName="times" />
          </IconButton>
        </FormLabel>
        <FormLabel
          data-tip="Uncheck Audit Allowed for all roles"
          className={classes.check}
        >
          <IconButton
            disabled={isViewOnlyUser}
            data-testid="datasource-access-uncheckall-audit"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { auditAllowed: 'N' });
              });
            }}
          >
            <span aria-hidden="false" style={{ display: 'none' }}>
              Uncheck Audit Allowed for all roles
            </span>
            <Icon disabled={isViewOnlyUser} iconName="times" />
          </IconButton>
        </FormLabel>
        <FormLabel
          data-tip="Uncheck Export for all roles"
          className={classes.check}
        >
          <IconButton
            disabled={isViewOnlyUser}
            data-testid="datasource-access-uncheckall-export"
            onClick={(e) => {
              store.recordsOrder().forEach((id) => {
                store.updateRecord(id, { exportAllowed: 'N' });
              });
            }}
          >
            <span aria-hidden="false" style={{ display: 'none' }}>
              Uncheck Export for all roles
            </span>
            <Icon disabled={isViewOnlyUser} iconName="times" />
          </IconButton>
        </FormLabel>
        <FormLabel sx={{ visibility: 'hidden' }} className={classes.check} />
      </ListItem>
      <ListItem className={classes.header} dense style={{ width, height: 40 }}>
        <ListItemText primary="Role" />
        <FormLabel data-tip="View Allowed" className={classes.check}>
          <Icon iconName="eye" />
        </FormLabel>
        <FormLabel data-tip="Insert Allowed" className={classes.check}>
          <Icon iconName="plus" />
        </FormLabel>
        <FormLabel data-tip="Update Allowed" className={classes.check}>
          <Icon iconName="edit" />
        </FormLabel>
        <FormLabel data-tip="Delete Allowed" className={classes.check}>
          <Icon iconName="trash" />
        </FormLabel>
        <FormLabel data-tip="Audit Allowed" className={classes.check}>
          <Icon iconName="ballot-check" />
        </FormLabel>
        <FormLabel data-tip="Export Allowed" className={classes.check}>
          <Icon iconName="file-export" />
        </FormLabel>
        <FormLabel data-tip="Public Download Allowed" className={classes.check}>
          <Icon iconName="paperclip" />
        </FormLabel>
      </ListItem>
      <VirtualList
        CellComponent={({ rowIndex, style }) => (
          <AppCell
            style={style}
            label={records[roles[rowIndex]].roleName}
            value={roles[rowIndex]}
            store={store}
            isViewOnlyUser={isViewOnlyUser}
          />
        )}
        rowHeight={40}
        height={height - 120 - 16}
        totalRows={roles.length}
        width={width}
        custom
      />
    </List>
  );
};

export default DataSourceAccess;
